import React, { useState, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
);

/**
 * MonthlyIncomeSpendingChart Component
 * 
 * Displays monthly income (positive/green bars) and spending (negative/red bars)
 * Each month shows independently (not cumulative)
 * 
 * @param {Object} props Component props
 * @param {Array} props.incomeTrends Array of income trend data from backend
 * @param {Array} props.spendingTrends Array of spending trend data from backend
 * @param {Array} props.recentActivity Array of actual transactions (for accurate "Actual" mode)
 * @param {boolean} props.isLoading Loading state indicator
 * @param {number} props.projectedMonthlyIncome Monthly projected income from Income sources
 * @returns {JSX.Element} MonthlyIncomeSpendingChart component
 */
const MonthlyIncomeSpendingChart = ({ 
  incomeTrends = [], 
  spendingTrends = [], 
  recentActivity = [],
  isLoading = false,
  projectedMonthlyIncome = 0
}) => {
  const [dataMode, setDataMode] = useState('projected'); // 'projected' or 'actual'
  
  // Helper function to check if transaction is a transfer (same as Transaction table logic)
  const isTransfer = (tx) => {
    if (!tx) return false;
    const amount = tx.amount || 0;
    const type = (tx.type || '').toLowerCase();
    const description = (tx.description || '').toLowerCase();
    
    if (type === 'transfer') return true;
    if (type === 'payment' && amount > 0) return true;
    
    const transferKeywords = ['transfer', 'autopay', 'automatic payment', 'payment - thank', 'payment thank'];
    if (transferKeywords.some(keyword => description.includes(keyword))) {
      return true;
    }
    
    return false;
  };
  
  // Calculate actual income and spending from transactions (same logic as Transaction table)
  const calculateActualFromTransactions = useMemo(() => {
    if (!recentActivity || recentActivity.length === 0) {
      return { incomeByMonth: {}, spendingByMonth: {} };
    }
    
    // Filter valid transactions (same as ExpenseStatsDashboard)
    const validTransactions = recentActivity.filter(tx => {
      if (!tx || !tx.date) return false;
      const amount = tx.amount || 0;
      const type = (tx.type || '').toLowerCase();
      if (type === 'expense' || type === 'bills' || type === 'payment') {
        return true;
      }
      return amount !== 0;
    });
    
    // Calculate income by month (EXCLUDE transfers - same as Transaction table)
    const incomeByMonth = {};
    validTransactions.forEach(tx => {
      if (isTransfer(tx)) return;
      
      const amount = tx.amount || 0;
      const type = (tx.type || '').toLowerCase();
      
      // Income: positive amount OR type='income' (EXCLUDE transfers)
      if ((amount > 0 || type === 'income') && !isTransfer(tx)) {
        const monthKey = new Date(tx.date).toISOString().slice(0, 7); // YYYY-MM
        incomeByMonth[monthKey] = (incomeByMonth[monthKey] || 0) + Math.abs(amount);
      }
    });
    
    // Calculate spending by month (EXCLUDE transfers - same as Transaction table)
    const spendingByMonth = {};
    validTransactions.forEach(tx => {
      if (isTransfer(tx)) return;
      
      const amount = tx.amount || 0;
      const type = (tx.type || '').toLowerCase();
      
      // Spending: negative amount OR type='expense' OR type='bills' OR credit card purchase
      const isExpense = (amount < 0 && type !== 'transfer') || 
                       type === 'expense' || 
                       type === 'bills' ||
                       (type === 'credit' && amount > 0); // Credit card purchases
      
      if (isExpense) {
        const monthKey = new Date(tx.date).toISOString().slice(0, 7); // YYYY-MM
        const expenseAmount = type === 'credit' ? amount : Math.abs(amount);
        spendingByMonth[monthKey] = (spendingByMonth[monthKey] || 0) + expenseAmount;
      }
    });
    
    return { incomeByMonth, spendingByMonth };
  }, [recentActivity]);

  // Combine all months from datasets AND recentActivity (Transaction table)
  const allMonths = useMemo(() => {
    const monthSet = new Set();
    
    // Add months from backend trends
    incomeTrends.forEach(item => monthSet.add(item.month));
    spendingTrends.forEach(item => monthSet.add(item.month));
    
    // Add months from recentActivity (Transaction table) - this is the source of truth for "Actual" mode
    if (recentActivity && recentActivity.length > 0) {
      recentActivity.forEach(tx => {
        if (tx && tx.date) {
          try {
            const monthKey = new Date(tx.date).toISOString().slice(0, 7); // YYYY-MM
            monthSet.add(monthKey);
          } catch (e) {
            // Skip invalid dates
          }
        }
      });
    }
    
    return Array.from(monthSet).sort();
  }, [incomeTrends, spendingTrends, recentActivity]);

  // Get income and spending data for each month
  const { incomeData, spendingData } = useMemo(() => {
    const income = [];
    const spending = [];

    allMonths.forEach(month => {
      if (dataMode === 'projected') {
        // Use projected monthly income (constant)
        income.push(projectedMonthlyIncome);
      } else {
        // ACTUAL MODE: Use backend incomeTrends (which now excludes transfers and matches Transaction table logic)
        // Backend trends have all 6 months of data and use same calculation as Transaction table
        const incomeItem = incomeTrends.find(item => item.month === month);
        income.push(incomeItem ? incomeItem.amount : 0);
      }

      // Spending: Always use backend spendingTrends (which now excludes transfers and matches Transaction table logic)
      // Backend trends have all 6 months of data and use same calculation as Transaction table
      const spendingItem = spendingTrends.find(item => item.month === month);
      spending.push(spendingItem ? -spendingItem.amount : 0); // Negative for display
    });

    console.log('MonthlyIncomeSpendingChart - Calculation:', {
      dataMode,
      incomeData: income,
      spendingData: spending,
      incomeTrends: incomeTrends,
      spendingTrends: spendingTrends,
      allMonths,
      note: 'Backend trends should now exclude transfers and match Transaction table logic'
    });

    return { incomeData: income, spendingData: spending };
  }, [allMonths, incomeTrends, spendingTrends, dataMode, projectedMonthlyIncome]);

  // Format months for display (MMM YY)
  const formattedMonths = allMonths.map(month => {
    const [year, monthNum] = month.split('-');
    const date = new Date(year, parseInt(monthNum) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  });

  const data = {
    labels: formattedMonths,
    datasets: [
      {
        type: 'bar',
        label: dataMode === 'projected' ? 'Projected Income' : 'Actual Income',
        data: incomeData,
        backgroundColor: 'rgba(34, 197, 94, 0.7)', // Green
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 1,
        borderRadius: 6,
        order: 1
      },
      {
        type: 'bar',
        label: 'Spending',
        data: spendingData,
        backgroundColor: 'rgba(239, 68, 68, 0.7)', // Red
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 1,
        borderRadius: 6,
        order: 2
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      datalabels: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = Math.abs(context.parsed.y);
            label += new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value);
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11
          }
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: true
        },
        ticks: {
          font: {
            size: 11
          },
          callback: function(value) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(Math.abs(value));
          }
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-2">
            <div className="h-2 w-48 bg-gray-200 rounded"></div>
            <div className="h-2 w-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (allMonths.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-400 text-sm">No data available</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Toggle for Projected vs Actual */}
      <div className="flex justify-end mb-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 sm:p-1">
          <button
            onClick={() => setDataMode('projected')}
            className={`px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-sm font-medium rounded-md transition-colors ${
              dataMode === 'projected'
                ? 'bg-violet-600 text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <span className="sm:hidden">P</span>
            <span className="hidden sm:inline">Projected</span>
          </button>
          <button
            onClick={() => setDataMode('actual')}
            className={`px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-sm font-medium rounded-md transition-colors ${
              dataMode === 'actual'
                ? 'bg-violet-600 text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            <span className="sm:hidden">A</span>
            <span className="hidden sm:inline">Actual</span>
          </button>
        </div>
      </div>
      
      {/* Chart */}
      <div className="flex-1">
        <Chart type="bar" data={data} options={options} key={dataMode + JSON.stringify(allMonths)} />
      </div>
    </div>
  );
};

export default MonthlyIncomeSpendingChart;

